/* Comment plate (sections/comment-plate.liquid). Port of the PORTED JS in
   mockups/comment-plate 08.10.26/comment-plate.html.
   The Liquid renders every tile (comments as native images in buttons, figures as theme assets)
   with its node name in data-cp-node; this script holds the plate description, solves it and
   positions the tiles. The lead tile holds one comment per persona; the section's inline script
   (window.kgIntentChoose, the one persona rule) leaves one of them visible, and the plate is
   re-solved whenever it chooses again (kg:persona-chosen).
   The phone zoom plays once per page load: the first time scroll reaches the whole plate it
   settles there, and the section then scrolls past at the whole plate with no zoom.
   Debug: ?cp_seek=0, 0.5 or 1 (levels 1, 2, 3) or KGPlate.seek(p); seek(NaN) hands control back
   to scroll. */
(function () {
  'use strict';

  if (window.KGCommentPlate) return;

  /* ---------- Plate geometry, in plate units ----------
     The plate is 100 units wide and one unit is 1cqi of the frame, so the seam (one unit, the CSS
     --comment-plate-seam) is 1% of the plate width. Four columns and three seams. */
  var PLATE_WIDTH = 100;
  var SEAM = 1;
  var COLUMN_COUNT = 4;
  var COLUMN_WIDTH = (PLATE_WIDTH - (COLUMN_COUNT - 1) * SEAM) / COLUMN_COUNT;

  /* The three heights the layout must meet exactly: the lead stack (level 1), the centre block
     from the centre comment down to the festival (level 2) and the whole plate (level 3). */
  var LEVEL_ONE = 'levelOne';
  var LEVEL_TWO = 'levelTwo';
  var LEVEL_THREE = 'levelThree';

  /* The static plate (750px and up, or reduced motion): one fixed design 1.54 plate widths tall,
     the v4b proportions (lead stack 34, centre block 75). */
  var STATIC_TARGETS = { levelOne: 34, levelTwo: 75, levelThree: 154 };

  /* How engravings share slack in a stack. Each starts at its fit height (the height at which the
     figure fills the tile's width). Spare height goes mostly to portrait figures, which grow into
     it; landscape, round and frieze figures take less so none floats in white. When height is
     short, portraits give it up first. */
  var GROW_WEIGHT_BY_STYLE = { portrait: 1, round: 0.5, landscape: 0.5, frieze: 0.5 };
  var SQUEEZE_WEIGHT_BY_STYLE = { portrait: 1, round: 0.1, landscape: 0.1, frieze: 0.1 };
  var HEIGHT_TOLERANCE = 1e-6;

  /* ---------- Plate description ----------
     One data structure; the solver below reads it and knows no tile by name. A column stacks its
     children with a seam between them; a row sets children side by side (each with a column span)
     at one shared height. Comments have a fixed height (their screenshot at the tile width;
     `sizeWith` makes mirrored partners share the taller one). Engravings are flexible: the figure
     is contained, so the tile absorbs slack (`sizeWith` makes partners share one fit height).
     `target` pins a node to one of the three level heights; `anchor` marks the tile each zoom level
     frames; `minRatio` marks a tall-only node, present only when the plate area's height to width
     ratio is at least that. Comment nodes are named by placement (the block's placement setting);
     engraving nodes by figure. */
  function comment(placement, options) {
    return Object.assign({ kind: 'comment', name: placement, sizeWith: [placement] }, options);
  }
  function engraving(figureKey, options) {
    return Object.assign({ kind: 'engraving', name: figureKey, sizeWith: [figureKey] }, options);
  }
  function stack(figureKey, placement, options) {
    return Object.assign({ kind: 'stack', name: figureKey, placement: placement }, options);
  }
  function column(children, options) { return Object.assign({ kind: 'column', children: children }, options); }
  function row(children, options) { return Object.assign({ kind: 'row', children: children }, options); }

  /* Column 1 and column 4, top to bottom: each entry is a mirrored pair, left then right. */
  var SIDE_PAIRS = [
    { kind: 'comment', pair: ['left-1', 'right-1'] },
    { kind: 'engraving', pair: ['zeus', 'poseidon'] },
    { kind: 'comment', pair: ['left-2', 'right-2'] },
    { kind: 'engraving', pair: ['pandora', 'clio'] },
    { kind: 'comment', pair: ['left-3', 'right-3'] },
    { kind: 'engraving', pair: ['apeliotes', 'boreas'] },
    { kind: 'comment', pair: ['left-4', 'right-4'] },
    { kind: 'engraving', pair: ['nymphs', 'dioscuri'] },
    { kind: 'comment', pair: ['left-5', 'right-5'] }
  ];

  function sideColumn(side) {
    return column(SIDE_PAIRS.map(function (entry) {
      var build = entry.kind === 'comment' ? comment : engraving;
      return build(entry.pair[side], { sizeWith: entry.pair });
    }), { span: 1 });
  }

  /* The plate mirrors about its vertical centre line; the two 3-column comments and their
     1-column rivers answer each other by rotation. The centre block (level 2) is the centre
     comment, the lead stack (level 1: Saturnus over the persona's lead) beside Rhea over its
     comment, and the festival one seam beneath both, its figure at the top of its tile so the
     picture always starts one seam under the comments. On tall phones the Dionysian frieze closes
     the block under the festival, and Hypnos and Thanatos join the centre below it. */
  var PLATE_LAYOUT = column([
    row([
      comment('top', { span: 3, sizeWith: ['top', 'foot'] }),
      engraving('nilus', { span: 1, sizeWith: ['nilus', 'tiber'] })
    ]),
    row([
      sideColumn(0),
      column([
        comment('centre-top'),
        engraving('hours'),
        column([
          comment('centre', { anchor: LEVEL_TWO }),
          row([
            stack('saturnus', 'lead', { span: 1, anchor: LEVEL_ONE }),
            stack('rhea', 'beside-lead', { span: 1 })
          ], { target: LEVEL_ONE }),
          engraving('festival'),
          engraving('orgies', { minRatio: 1.85 })
        ], { target: LEVEL_TWO }),
        row([
          engraving('hypnos', { span: 1, sizeWith: ['hypnos', 'thanatos'] }),
          engraving('thanatos', { span: 1, sizeWith: ['hypnos', 'thanatos'] })
        ], { minRatio: 1.85 }),
        engraving('nereid')
      ], { span: 2 }),
      sideColumn(1)
    ]),
    row([
      engraving('tiber', { span: 1, sizeWith: ['nilus', 'tiber'] }),
      comment('foot', { span: 3, sizeWith: ['top', 'foot'] })
    ])
  ], { target: LEVEL_THREE });

  function tileKey(node) { return node.kind + ':' + node.name; }

  function leafNodes(node, list) {
    list = list || [];
    if (node.children) node.children.forEach(function (child) { leafNodes(child, list); });
    else list.push(node);
    return list;
  }

  /* ---------- Solver ----------
     `shape` reads the rendered tiles: comment height over width by placement, figure height over
     width and style by figure key. */

  function spanWidth(span) { return span * COLUMN_WIDTH + (span - 1) * SEAM; }

  function withoutTallOnly(node, areaRatio) {
    if (!node.children) return node;
    var children = node.children
      .filter(function (child) { return !(child.minRatio > areaRatio); })
      .map(function (child) { return withoutTallOnly(child, areaRatio); });
    return Object.assign({}, node, { children: children });
  }

  function mean(values) { return values.reduce(function (sum, value) { return sum + value; }, 0) / values.length; }

  function Solver(shape) { this.shape = shape; }

  Solver.prototype.commentHeight = function (placement, width) {
    var ratio = this.shape.comments[placement];
    if (!ratio) throw new Error('Comment plate: no comment in placement "' + placement + '"');
    return width * ratio;
  };

  Solver.prototype.figureFitHeight = function (figureKey, width) {
    var figure = this.shape.figures[figureKey];
    if (!figure) throw new Error('Comment plate: no figure "' + figureKey + '"');
    return width * figure.ratio;
  };

  /* The height a node has whatever its parent offers, or null when it is flexible. */
  Solver.prototype.fixedHeight = function (node, width, targets) {
    var solver = this;
    if (node.target) return targets[node.target];
    if (node.kind === 'comment') return Math.max.apply(null, node.sizeWith.map(function (placement) { return solver.commentHeight(placement, width); }));
    if (node.kind === 'column') return this.fixedColumnHeight(node, width, targets);
    if (node.kind === 'row') return this.fixedRowHeight(node, targets);
    return null;
  };

  Solver.prototype.fixedColumnHeight = function (node, width, targets) {
    var solver = this;
    var heights = node.children.map(function (child) { return solver.fixedHeight(child, width, targets); });
    if (heights.indexOf(null) !== -1) return null;
    return heights.reduce(function (sum, height) { return sum + height; }, 0) + SEAM * (heights.length - 1);
  };

  Solver.prototype.fixedRowHeight = function (node, targets) {
    var solver = this;
    var heights = node.children
      .map(function (child) { return solver.fixedHeight(child, spanWidth(child.span), targets); })
      .filter(function (height) { return height !== null; });
    return heights.length ? Math.max.apply(null, heights) : null;
  };

  /* Fit height and weights of a flexible node: an engraving, or a row of engravings. */
  Solver.prototype.flexProfile = function (node, width) {
    if (node.kind === 'engraving') return this.engravingProfile(node, width);
    if (node.kind === 'row') return this.rowProfile(node);
    throw new Error('Comment plate: a ' + node.kind + ' cannot share slack; give it a target');
  };

  Solver.prototype.engravingProfile = function (node, width) {
    var solver = this;
    var style = this.shape.figures[node.name].style;
    return {
      fit: mean(node.sizeWith.map(function (key) { return solver.figureFitHeight(key, width); })),
      grow: GROW_WEIGHT_BY_STYLE[style] || 0.5,
      squeeze: SQUEEZE_WEIGHT_BY_STYLE[style] || 0.1
    };
  };

  Solver.prototype.rowProfile = function (node) {
    var solver = this;
    var profiles = node.children.map(function (child) { return solver.flexProfile(child, spanWidth(child.span)); });
    return {
      fit: mean(profiles.map(function (profile) { return profile.fit; })),
      grow: mean(profiles.map(function (profile) { return profile.grow; })),
      squeeze: mean(profiles.map(function (profile) { return profile.squeeze; }))
    };
  };

  /* Linear share: height = fit * (1 + weight * t), with t chosen so the heights sum exactly. */
  function shareSlack(profiles, available) {
    var slack = available - profiles.reduce(function (sum, profile) { return sum + profile.fit; }, 0);
    var weightKey = slack >= 0 ? 'grow' : 'squeeze';
    var weighted = profiles.reduce(function (sum, profile) { return sum + profile.fit * profile[weightKey]; }, 0);
    if (!weighted) {
      if (Math.abs(slack) > HEIGHT_TOLERANCE) throw new Error('Comment plate: a stack is ' + slack.toFixed(3) + ' units off its target with nothing flexible');
      return profiles.map(function (profile) { return profile.fit; });
    }
    var stretch = slack / weighted;
    return profiles.map(function (profile) { return profile.fit * (1 + profile[weightKey] * stretch); });
  }

  Solver.prototype.childHeights = function (node, box, targets) {
    var solver = this;
    var fixed = node.children.map(function (child) { return solver.fixedHeight(child, box.width, targets); });
    var flexible = node.children.filter(function (child, index) { return fixed[index] === null; });
    var fixedTotal = fixed.reduce(function (sum, height) { return sum + (height || 0); }, 0);
    var available = box.height - fixedTotal - SEAM * (node.children.length - 1);
    var shared = flexible.length === 1 ? [available] : shareSlack(flexible.map(function (child) { return solver.flexProfile(child, box.width); }), available);
    return fixed.map(function (height) { return height === null ? shared.shift() : height; });
  };

  Solver.prototype.placeColumn = function (node, box, targets, placed) {
    var solver = this;
    var heights = this.childHeights(node, box, targets);
    var top = box.y;
    node.children.forEach(function (child, index) {
      solver.placeNode(child, { x: box.x, y: top, width: box.width, height: heights[index] }, targets, placed);
      top += heights[index] + SEAM;
    });
  };

  Solver.prototype.placeRow = function (node, box, targets, placed) {
    var solver = this;
    var left = box.x;
    node.children.forEach(function (child) {
      var width = spanWidth(child.span);
      solver.placeNode(child, { x: left, y: box.y, width: width, height: box.height }, targets, placed);
      left += width + SEAM;
    });
  };

  /* The least a tile needs: a comment its screenshot, a stack its comment and a seam. */
  Solver.prototype.minimumHeight = function (node, width, targets) {
    if (node.kind === 'stack') return this.commentHeight(node.placement, width) + SEAM;
    return this.fixedHeight(node, width, targets);
  };

  Solver.prototype.placeLeaf = function (node, box, targets, placed) {
    var needed = this.minimumHeight(node, box.width, targets);
    if (needed !== null && needed > box.height + HEIGHT_TOLERANCE) {
      throw new Error('Comment plate: ' + tileKey(node) + ' needs ' + needed.toFixed(3) + ' units, has ' + box.height.toFixed(3));
    }
    placed.push({ node: node, box: box });
  };

  Solver.prototype.placeNode = function (node, box, targets, placed) {
    if (node.kind === 'column') return this.placeColumn(node, box, targets, placed);
    if (node.kind === 'row') return this.placeRow(node, box, targets, placed);
    return this.placeLeaf(node, box, targets, placed);
  };

  /* Every tile as a box in plate units, for the given level heights and plate-area ratio. */
  Solver.prototype.solve = function (targets, areaRatio) {
    var placed = [];
    this.placeNode(withoutTallOnly(PLATE_LAYOUT, areaRatio), { x: 0, y: 0, width: PLATE_WIDTH, height: targets[LEVEL_THREE] }, targets, placed);
    return placed;
  };

  /* ---------- Phone zoom ---------- */

  var ZOOM_QUERY = '(max-width: 749px) and (prefers-reduced-motion: no-preference)';
  /* On phones the plate takes the plate area's own proportions: the layout is solved for the
     area's height over its width, kept inside this range (beyond it the fit is no longer exact). */
  var AREA_RATIO_MIN = 1.4;
  var AREA_RATIO_MAX = 2.2;
  /* Columns that fill the screen's width at levels 1 and 2 (the anchors' spans). */
  var SPAN_AT_LEVEL_ONE = 1;
  var SPAN_AT_LEVEL_TWO = 2;
  /* Air between the foot of each level's content and the foot of the screen, in px. It must stay
     under one seam at level 2 (screen width / 49.5, 6.46px at 320) or the next tile would show. */
  var BOTTOM_INSET_PX = 6;
  /* Scroll track, in stage heights: hold on one column, zoom to two columns, hold, zoom to the
     whole plate, hold, then the stage releases. */
  var HOLD_ON_ONE_COLUMN = 0.3;
  var ZOOM_TO_TWO_COLUMNS = 1;
  var HOLD_ON_TWO_COLUMNS = 0.3;
  var ZOOM_TO_WHOLE_PLATE = 1;
  var HOLD_ON_WHOLE_PLATE = 0.4;
  /* Zoom progress p runs 0..1 over the two zooms: p = 0 is level 1, one column;
     p = LEVEL_TWO_PROGRESS is level 2, two columns; p = 1 is level 3, the whole plate. */
  var LEVEL_TWO_PROGRESS = 0.5;
  var RESIZE_DEBOUNCE_MS = 150;
  /* Where scrollend is missing, scroll counts as resting this long after its last event. */
  var SCROLL_REST_MS = 120;
  /* Images start loading once the section is this close to the screen. */
  var NEAR_MARGIN = '150% 0px';

  function clamp(value, min, max) { return Math.min(max, Math.max(min, value)); }
  function clamp01(value) { return clamp(value, 0, 1); }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function debounce(callback, waitMs) {
    var timer = null;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(callback, waitMs);
    };
  }

  function readSeekParam() {
    var raw = null;
    try { raw = new URLSearchParams(window.location.search).get('cp_seek'); } catch (error) { raw = null; }
    return raw === null ? NaN : clamp01(parseFloat(raw));
  }

  /* The three level heights for a phone's plate area (px), in plate units. Level 1 shows one
     column at the screen's width, level 2 two columns, level 3 the plate inside the gutter; each
     is as tall as the area less the bottom inset. */
  function phoneFit(area, gutter) {
    var areaRatio = clamp(area.height / area.width, AREA_RATIO_MIN, AREA_RATIO_MAX);
    var contentHeight = areaRatio * area.width - BOTTOM_INSET_PX;
    return {
      areaRatio: areaRatio,
      targets: {
        levelOne: contentHeight * spanWidth(SPAN_AT_LEVEL_ONE) / area.width,
        levelTwo: contentHeight * spanWidth(SPAN_AT_LEVEL_TWO) / area.width,
        levelThree: contentHeight * PLATE_WIDTH / (area.width - 2 * gutter)
      }
    };
  }

  /* Measured while the frame has no transform (applyZoom clears it), from client rects rather
     than offsets, which round to whole pixels and would leave the zoom a hair off. */
  function boxInFrame(element, frameBox) {
    var box = element.getBoundingClientRect();
    return { left: box.left - frameBox.left, top: box.top - frameBox.top, width: box.width, height: box.height };
  }

  /* Levels 1 and 2: the anchor tile fills the viewport width with its top at the viewport top;
     the layout makes what lies beneath end one inset above the foot of the screen. */
  function windowOnAnchor(viewport, anchor) {
    var scale = viewport.width / anchor.width;
    return { scale: scale, x: -scale * anchor.left, y: -scale * anchor.top };
  }

  /* Level 3: the whole plate inside the gutter at the sides and the inset at the foot, top
     aligned under the heading. The layout makes both limits meet; min() guards the clamp. */
  function wholePlateTransform(viewport, plate, gutter) {
    var availableWidth = viewport.width - 2 * gutter;
    var availableHeight = viewport.height - BOTTOM_INSET_PX;
    var scale = Math.min(availableWidth / plate.width, availableHeight / plate.height);
    return { scale: scale, x: (viewport.width - scale * plate.width) / 2, y: 0 };
  }

  /* Geometric zoom about the one point of the plate that stays still on screen, so each move
     reads as a single zoom rather than a scale plus a pan. */
  function zoomBetween(from, to, t) {
    var eased = easeInOutCubic(t);
    var scale = from.scale * Math.pow(to.scale / from.scale, eased);
    var scaleChange = from.scale - to.scale;
    if (Math.abs(scaleChange) < 1e-6) {
      return { scale: scale, x: from.x + (to.x - from.x) * eased, y: from.y + (to.y - from.y) * eased };
    }
    var shift = from.scale - scale;
    return {
      scale: scale,
      x: from.x + shift * (to.x - from.x) / scaleChange,
      y: from.y + shift * (to.y - from.y) / scaleChange
    };
  }

  /* Scroll offset into the track, in stage heights, mapped to zoom progress with the holds flat. */
  function progressAtOffset(offset) {
    var firstZoomStart = HOLD_ON_ONE_COLUMN;
    var secondZoomStart = firstZoomStart + ZOOM_TO_TWO_COLUMNS + HOLD_ON_TWO_COLUMNS;
    if (offset < secondZoomStart) {
      return LEVEL_TWO_PROGRESS * clamp01((offset - firstZoomStart) / ZOOM_TO_TWO_COLUMNS);
    }
    return LEVEL_TWO_PROGRESS + (1 - LEVEL_TWO_PROGRESS) * clamp01((offset - secondZoomStart) / ZOOM_TO_WHOLE_PLATE);
  }

  /* The inverse, landing a level in the middle of its hold so a seek shows a resting frame. */
  function offsetAtProgress(progress) {
    var firstZoomEnd = HOLD_ON_ONE_COLUMN + ZOOM_TO_TWO_COLUMNS;
    var secondZoomStart = firstZoomEnd + HOLD_ON_TWO_COLUMNS;
    if (progress <= 0) return HOLD_ON_ONE_COLUMN / 2;
    if (progress < LEVEL_TWO_PROGRESS) return HOLD_ON_ONE_COLUMN + ZOOM_TO_TWO_COLUMNS * progress / LEVEL_TWO_PROGRESS;
    if (progress === LEVEL_TWO_PROGRESS) return firstZoomEnd + HOLD_ON_TWO_COLUMNS / 2;
    if (progress < 1) return secondZoomStart + ZOOM_TO_WHOLE_PLATE * (progress - LEVEL_TWO_PROGRESS) / (1 - LEVEL_TWO_PROGRESS);
    return secondZoomStart + ZOOM_TO_WHOLE_PLATE + HOLD_ON_WHOLE_PLATE / 2;
  }

  function positionTile(element, box) {
    element.style.setProperty('--tile-x', box.x);
    element.style.setProperty('--tile-y', box.y);
    element.style.setProperty('--tile-width', box.width);
    element.style.setProperty('--tile-height', box.height);
  }

  function clearTile(element) {
    ['--tile-x', '--tile-y', '--tile-width', '--tile-height'].forEach(function (name) { element.style.removeProperty(name); });
  }

  /* Reading and tab order runs across the plate, top row first, as the eye reads it. */
  function inReadingOrder(placed) {
    return placed.slice().sort(function (first, second) {
      return (first.box.y - second.box.y) || (first.box.x - second.box.x);
    });
  }

  function isShown(element) { return !element.closest('[hidden]'); }

  /* The comment in a tile that the persona choice left visible. */
  function shownComment(tile) {
    var buttons = tile.querySelectorAll('.comment-plate__comment');
    for (var index = 0; index < buttons.length; index += 1) {
      if (!buttons[index].hasAttribute('hidden')) return buttons[index];
    }
    return null;
  }

  function ratioOf(element) {
    var width = parseFloat(element.getAttribute('data-cp-width'));
    var height = parseFloat(element.getAttribute('data-cp-height'));
    return width > 0 && height > 0 ? height / width : 0;
  }

  /* ---------- One plate ---------- */

  function CommentPlate(root) {
    this.root = root;
    this.track = root.querySelector('[data-cp-track]');
    this.stage = root.querySelector('[data-cp-stage]');
    this.header = root.querySelector('[data-cp-header]');
    this.viewport = root.querySelector('[data-cp-viewport]');
    this.frame = root.querySelector('[data-cp-frame]');
    this.plate = root.querySelector('[data-cp-plate]');
    this.dialog = root.querySelector('[data-cp-dialog]');
    this.zoomQuery = window.matchMedia(ZOOM_QUERY);
    this.controller = 'AbortController' in window ? new AbortController() : null;
    this.seekOverride = NaN;
    this.isZoomMode = false;
    this.isLaidOut = false;
    this.isNear = false;
    this.frameRequested = false;
    this.lastOpener = null;
    this.isSettled = false;
    this.isTrackShortened = false;
    this.isShortenPending = false;
    this.restTimer = null;
    this.geometry = null;
    this.levelOneAnchor = null;
    this.levelTwoAnchor = null;
  }

  CommentPlate.prototype.listen = function (target, type, handler, options) {
    var settings = Object.assign({}, options || {});
    if (this.controller) settings.signal = this.controller.signal;
    target.addEventListener(type, handler, settings);
  };

  CommentPlate.prototype.init = function () {
    if (!this.plate || !this.frame) return;
    this.bindDialog();
    this.watchNearness();
    this.applyMode();
    this.listen(window, 'scroll', this.requestFrame.bind(this), { passive: true });
    this.listen(window, 'resize', debounce(this.refresh.bind(this), RESIZE_DEBOUNCE_MS));
    this.listen(this.zoomQuery, 'change', this.refresh.bind(this));
    /* The section's persona script chose a different lead: its height changes the lead stack. */
    this.listen(this.root, 'kg:persona-chosen', this.refresh.bind(this));
    this.seekFromUrl();
    /* Web fonts reflow the heading above the viewport, which changes the plate area. */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.refresh.bind(this));
    this.listen(window, 'load', this.refresh.bind(this));
  };

  CommentPlate.prototype.destroy = function () {
    if (this.controller) this.controller.abort();
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.restTimer);
    this.isZoomMode = false;
  };

  CommentPlate.prototype.refresh = function () {
    this.applyMode();
    this.seekFromUrl();
  };

  /* What the solver needs from the rendered tiles; null when a placement is empty, in which case
     the plate stays a plain grid. */
  CommentPlate.prototype.readShape = function () {
    var plate = this.plate;
    var shape = { comments: {}, figures: {} };
    var complete = true;
    leafNodes(PLATE_LAYOUT).forEach(function (node) {
      var tile = plate.querySelector('[data-cp-node="' + tileKey(node) + '"]');
      if (!tile) { complete = false; return; }
      if (node.kind !== 'comment') {
        var image = tile.querySelector('.comment-plate__engraving');
        if (!image) { complete = false; return; }
        shape.figures[node.name] = { ratio: ratioOf(image), style: image.getAttribute('data-cp-style') || 'landscape' };
      }
      if (node.kind !== 'engraving') {
        var button = shownComment(tile);
        var ratio = button ? ratioOf(button) : 0;
        if (!ratio) { complete = false; return; }
        shape.comments[node.kind === 'stack' ? node.placement : node.name] = ratio;
      }
    });
    return complete ? shape : null;
  };

  /* Mode: zoom on phones with motion allowed, else the static plate. */
  CommentPlate.prototype.applyMode = function () {
    var shape = this.readShape();
    if (!shape) return this.applyGrid();
    this.solver = new Solver(shape);
    this.isZoomMode = this.zoomQuery.matches;
    this.root.classList.toggle('comment-plate--zoom', this.isZoomMode);
    try {
      if (this.isZoomMode) this.applyZoom(); else this.applyStatic();
    } catch (error) {
      if (window.console) console.warn(error);
      this.applyGrid();
    }
  };

  /* No layout: the plain grid of comments the CSS shows before the script runs. */
  CommentPlate.prototype.applyGrid = function () {
    this.isZoomMode = false;
    this.isLaidOut = false;
    this.geometry = null;
    this.root.classList.remove('comment-plate--zoom');
    this.plate.classList.remove('comment-plate__plate--laid-out');
    this.track.style.height = '';
    this.frame.style.width = '';
    this.frame.style.transform = '';
    Array.prototype.forEach.call(this.plate.children, function (tile) {
      clearTile(tile);
      if (tile.hasAttribute('data-cp-tall-only')) tile.setAttribute('hidden', '');
    });
  };

  CommentPlate.prototype.applyStatic = function () {
    this.geometry = null;
    this.track.style.height = '';
    this.frame.style.width = '';
    this.frame.style.transform = '';
    this.layOut(STATIC_TARGETS, AREA_RATIO_MIN);
  };

  /* The frame is laid out at the size where one column is as wide as the viewport, so level 1
     draws at scale 1 and every later frame scales down: a scaled-up layer would blur. */
  CommentPlate.prototype.applyZoom = function () {
    this.frame.style.transform = '';
    var area = this.measureArea();
    var fit = phoneFit(area, this.gutterWidth());
    this.frame.style.width = area.width * PLATE_WIDTH / COLUMN_WIDTH + 'px';
    this.layOut(fit.targets, fit.areaRatio);
    this.geometry = this.measure(area);
    this.track.style.height = (this.isTrackShortened ? this.geometry.stageHeight : this.geometry.trackHeight) + 'px';
    this.render();
  };

  /* The viewport is the 100svh stage less the heading and its gap, so the area holds still while
     the browser bar comes and goes. */
  CommentPlate.prototype.measureArea = function () {
    var box = this.viewport.getBoundingClientRect();
    return { width: box.width, height: box.height };
  };

  /* Places every tile the layout uses, hides the tall-only ones it leaves out, and puts the tiles
     in reading order. Moving a node drops its focus, so the focused tile, if any, gets it back. */
  CommentPlate.prototype.layOut = function (targets, areaRatio) {
    var plate = this.plate;
    var placed = inReadingOrder(this.solver.solve(targets, areaRatio));
    var used = [];
    var anchors = {};
    placed.forEach(function (item) {
      var tile = plate.querySelector('[data-cp-node="' + tileKey(item.node) + '"]');
      positionTile(tile, item.box);
      tile.removeAttribute('hidden');
      if (item.node.anchor) anchors[item.node.anchor] = tile;
      used.push(tile);
    });
    var focused = document.activeElement;
    Array.prototype.slice.call(plate.children).forEach(function (tile) {
      if (used.indexOf(tile) === -1) { clearTile(tile); tile.setAttribute('hidden', ''); }
    });
    used.forEach(function (tile) { plate.append(tile); });
    if (plate.contains(focused) && document.activeElement !== focused) focused.focus({ preventScroll: true });
    plate.style.setProperty('--comment-plate-height', targets[LEVEL_THREE]);
    plate.classList.add('comment-plate__plate--laid-out');
    this.isLaidOut = true;
    this.levelOneAnchor = anchors[LEVEL_ONE];
    this.levelTwoAnchor = anchors[LEVEL_TWO];
    this.loadImagesIfNear();
  };

  /* Images carry loading="lazy"; inside the zoom frame most tiles sit outside the viewport's clip
     until the zoom reaches them, so once the section is near they are all asked for at once and
     the zoom never shows an empty tile. Hidden ones (other personas' leads, tall-only figures
     left out) are skipped. */
  CommentPlate.prototype.watchNearness = function () {
    if (!('IntersectionObserver' in window)) { this.isNear = true; return; }
    var plate = this;
    this.observer = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
      plate.isNear = true;
      plate.observer.disconnect();
      plate.loadImagesIfNear();
    }, { rootMargin: NEAR_MARGIN });
    this.observer.observe(this.root);
  };

  CommentPlate.prototype.loadImagesIfNear = function () {
    if (!this.isNear) return;
    Array.prototype.forEach.call(this.plate.querySelectorAll('img[loading="lazy"]'), function (image) {
      if (isShown(image)) image.setAttribute('loading', 'eager');
    });
  };

  CommentPlate.prototype.measure = function (area) {
    var frameBox = this.frame.getBoundingClientRect();
    var plateSize = { width: frameBox.width, height: frameBox.height };
    var stageHeight = this.stage.getBoundingClientRect().height;
    return {
      keyframes: [
        windowOnAnchor(area, boxInFrame(this.levelOneAnchor, frameBox)),
        windowOnAnchor(area, boxInFrame(this.levelTwoAnchor, frameBox)),
        wholePlateTransform(area, plateSize, this.gutterWidth())
      ],
      stageHeight: stageHeight,
      trackHeight: stageHeight * (1 + HOLD_ON_ONE_COLUMN + ZOOM_TO_TWO_COLUMNS + HOLD_ON_TWO_COLUMNS + ZOOM_TO_WHOLE_PLATE + HOLD_ON_WHOLE_PLATE),
      trackTop: this.track.getBoundingClientRect().top + window.scrollY
    };
  };

  /* The heading's side padding is the section gutter. */
  CommentPlate.prototype.gutterWidth = function () {
    return parseFloat(getComputedStyle(this.header).paddingLeft) || 0;
  };

  CommentPlate.prototype.transformAt = function (progress) {
    var keyframes = this.geometry.keyframes;
    if (progress <= LEVEL_TWO_PROGRESS) return zoomBetween(keyframes[0], keyframes[1], progress / LEVEL_TWO_PROGRESS);
    return zoomBetween(keyframes[1], keyframes[2], (progress - LEVEL_TWO_PROGRESS) / (1 - LEVEL_TWO_PROGRESS));
  };

  CommentPlate.prototype.currentProgress = function () {
    if (!Number.isNaN(this.seekOverride)) return this.seekOverride;
    if (this.isSettled) return 1;
    return progressAtOffset((window.scrollY - this.geometry.trackTop) / this.geometry.stageHeight);
  };

  /* Once the track is shortened there is nothing left for scroll to drive. */
  CommentPlate.prototype.requestFrame = function () {
    if (!this.isZoomMode || this.frameRequested || this.isTrackShortened) return;
    this.frameRequested = true;
    window.requestAnimationFrame(this.render.bind(this));
  };

  CommentPlate.prototype.render = function () {
    this.frameRequested = false;
    if (!this.isZoomMode || !this.geometry) return;
    var progress = this.currentProgress();
    var t = this.transformAt(progress);
    this.frame.style.transform = 'translate(' + t.x + 'px, ' + t.y + 'px) scale(' + t.scale + ')';
    if (progress >= 1 && Number.isNaN(this.seekOverride)) this.isSettled = true;
    if (this.isSettled && !this.isTrackShortened && !this.isStagePinned()) this.shortenWhenScrollRests();
  };

  CommentPlate.prototype.isStagePinned = function () {
    var scrolledIntoTrack = window.scrollY - this.geometry.trackTop;
    return scrolledIntoTrack > 0 && scrolledIntoTrack < this.geometry.trackHeight - this.geometry.stageHeight;
  };

  /* The track never shortens mid-scroll (a fling on a phone): it waits for scroll to rest, at
     scrollend or, where that event is missing, SCROLL_REST_MS after the last scroll event, and
     then only if the stage is still not pinned. */
  CommentPlate.prototype.shortenWhenScrollRests = function () {
    if (this.isShortenPending) return;
    this.isShortenPending = true;
    var plate = this;
    var waitForRest = function () {
      clearTimeout(plate.restTimer);
      plate.restTimer = setTimeout(rest, SCROLL_REST_MS);
    };
    var rest = function () {
      plate.isShortenPending = false;
      clearTimeout(plate.restTimer);
      window.removeEventListener('scroll', waitForRest);
      if (plate.isZoomMode && plate.geometry && !plate.isTrackShortened && !plate.isStagePinned()) plate.shortenTrack();
    };
    if ('onscrollend' in window) {
      this.listen(window, 'scrollend', rest, { once: true });
      return;
    }
    this.listen(window, 'scroll', waitForRest, { passive: true });
    waitForRest();
  };

  /* Settled, the stage needs no scroll track, so the track drops to the stage's height and the
     section scrolls past like any other. It waits until the stage is not pinned, so the first
     pass keeps its hold on the whole plate, and the page is scrolled by however far the stage
     moved, so nothing on screen jumps (measured, as scroll anchoring may already have moved it).
     The scroll is instant: the intent page sets scroll-behavior: smooth on the root, which would
     animate it after the track had already shrunk, so the page jumped and then slid back. */
  CommentPlate.prototype.shortenTrack = function () {
    var stageTopBefore = this.stage.getBoundingClientRect().top;
    this.isTrackShortened = true;
    this.track.style.height = this.geometry.stageHeight + 'px';
    var distance = this.stage.getBoundingClientRect().top - stageTopBefore;
    try {
      window.scrollBy({ top: distance, behavior: 'instant' });
    } catch (error) {
      window.scrollBy(0, distance);
    }
  };

  /* Debug: ?cp_seek=0..1 or KGPlate.seek(p) shows any frame (0.5 is level 2);
     seek(NaN) hands control back to scroll. */
  CommentPlate.prototype.seek = function (progress) {
    this.seekOverride = Number.isNaN(progress) ? NaN : clamp01(progress);
    this.render();
  };

  CommentPlate.prototype.seekFromUrl = function () {
    var progress = readSeekParam();
    if (Number.isNaN(progress)) return;
    this.seek(progress);
    if (this.isZoomMode && this.geometry) window.scrollTo(0, this.geometry.trackTop + offsetAtProgress(progress) * this.geometry.stageHeight);
  };

  /* ---------- Dialog ---------- */

  /* Escape is handled explicitly because one devtools run on the 4 Oct build saw the native
     cancel fail to close it; the cause was never found. */
  CommentPlate.prototype.bindDialog = function () {
    var plate = this;
    if (!this.dialog) return;
    this.listen(this.plate, 'click', function (event) {
      var button = event.target.closest('.comment-plate__comment');
      if (button) plate.openDialog(button);
    });
    var close = this.dialog.querySelector('[data-cp-close]');
    if (close) this.listen(close, 'click', function () { plate.dialog.close(); });
    this.listen(this.dialog, 'keydown', function (event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      plate.dialog.close();
    });
    this.listen(this.dialog, 'click', function (event) {
      if (event.target === plate.dialog) plate.dialog.close();
    });
    this.listen(this.dialog, 'close', this.removeDialogImage.bind(this));
    this.listen(this.dialog, 'close', this.returnFocus.bind(this));
  };

  CommentPlate.prototype.openDialog = function (button) {
    if (typeof this.dialog.showModal !== 'function') return;
    this.lastOpener = button;
    this.removeDialogImage();
    this.dialog.append(this.createDialogImage(button));
    this.dialog.showModal();
  };

  /* Built on open and removed on close, so no large image loads until it is asked for. */
  CommentPlate.prototype.createDialogImage = function (button) {
    var source = button.querySelector('img');
    var image = document.createElement('img');
    image.className = 'comment-plate__dialog-image';
    image.src = button.getAttribute('data-cp-large') || source.currentSrc || source.src;
    image.alt = source.alt;
    image.width = source.width;
    image.height = source.height;
    return image;
  };

  CommentPlate.prototype.removeDialogImage = function () {
    var image = this.dialog.querySelector('.comment-plate__dialog-image');
    if (image) image.remove();
  };

  /* preventScroll: a focus scroll would move the page and with it the zoom frame. */
  CommentPlate.prototype.returnFocus = function () {
    if (this.lastOpener) this.lastOpener.focus({ preventScroll: true });
  };

  /* ---------- Instances ---------- */

  var instances = [];

  function start(root) {
    if (!root || root.getAttribute('data-cp-ready') === 'true') return;
    root.setAttribute('data-cp-ready', 'true');
    var instance = new CommentPlate(root);
    instance.init();
    instances.push(instance);
  }

  function stop(container) {
    instances = instances.filter(function (instance) {
      if (!container.contains(instance.root)) return true;
      instance.destroy();
      return false;
    });
  }

  function startAll(container) {
    Array.prototype.forEach.call((container || document).querySelectorAll('[data-comment-plate]'), start);
  }

  window.KGCommentPlate = { start: start };
  window.KGPlate = {
    seek: function (progress) { instances.forEach(function (instance) { instance.seek(progress); }); }
  };

  document.addEventListener('shopify:section:unload', function (event) { stop(event.target); });
  document.addEventListener('shopify:section:load', function (event) { startAll(event.target); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { startAll(); });
  else startAll();
})();
